/**
 * Session token storage. Native: Keystore/Keychain + Bearer header (see
 * services/api.ts). Web: no-op; relies on the HttpOnly session cookie.
 */

import { isNativePlatform } from "./platform";

const SESSION_TOKEN_KEY = "knocknoc_session_token";

let _cachedToken: string | null | undefined; // undefined = not yet read

// The background poller (Android foreground service / iOS Network Extension)
// needs its own copy of the bearer; keep the plugin in sync on every set/clear.
async function pushTokenToIpChange(token: string): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { default: IpChange } = await import("./ip-change-plugin");
    await IpChange.setSessionToken({ token });
  } catch (e) {
    console.warn("[SessionToken] push to IpChange failed:", e);
  }
}

export async function getSessionToken(): Promise<string | null> {
  if (!isNativePlatform()) return null;
  if (_cachedToken !== undefined) return _cachedToken;

  try {
    const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
    const value = await SecureStorage.get(SESSION_TOKEN_KEY);
    _cachedToken = typeof value === "string" ? value : null;
  } catch (e) {
    console.warn("[SessionToken] read failed:", e);
    _cachedToken = null;
  }
  return _cachedToken;
}

export async function setSessionToken(token: string): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
    await SecureStorage.set(SESSION_TOKEN_KEY, token);
    _cachedToken = token;
  } catch (e) {
    console.warn("[SessionToken] write failed:", e);
  }
  await pushTokenToIpChange(token);
}

export async function clearSessionToken(): Promise<void> {
  _cachedToken = null;
  if (!isNativePlatform()) return;
  try {
    const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
    await SecureStorage.remove(SESSION_TOKEN_KEY);
  } catch (e) {
    console.warn("[SessionToken] remove failed:", e);
  }
  await pushTokenToIpChange("");
}

// Test-only: reset the in-memory cache so spec tests can simulate fresh state.
export function _resetSessionTokenCacheForTests(): void {
  _cachedToken = undefined;
}
